import { useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { listPartnerEsign, partnerEsignFileUrl, type EsignPartnerDocument } from '../api/esign';
import { ArcPanel } from '../components/arc/primitives/arc-primitives';
import { AffilmasterDetailSectionHeader } from '../affilmaster/AffilmasterDetailFields';
import './klient-smlouvy.css';

const DAY = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });

function day(value: string | null): string {
  if (!value) return '–';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '–' : DAY.format(d);
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function ContractIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 2.75h8l4 4V21.25H6z" />
      <path d="M14 2.75v4h4M9 11h6M9 15h4" />
    </svg>
  );
}

export function KlientSmlouvySection({ id, onOpenDocument }: { id: number; onOpenDocument?: (documentId: number) => void }) {
  const [rows, setRows] = useState<EsignPartnerDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    listPartnerEsign(id)
      .then((data) => { if (!cancelled) setRows(data); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Načtení smluv selhalo'); });
    return () => { cancelled = true; };
  }, [id]);

  if (error) return <div className="alert alert-danger mb-0">{error}</div>;
  if (!rows) return <div className="text-muted py-3"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>;
  if (rows.length === 0) return (
    <div className="klient-smlouvy">
      <AffilmasterDetailSectionHeader title="Smlouvy" description="Podepsané dokumenty klienta" />
      <div className="klient-smlouvy-empty">
        <span className="klient-smlouvy-empty-icon"><ContractIcon /></span>
        <div>
          <strong>Žádné eSign smlouvy</strong>
          <p>Tento klient zatím nemá žádnou smlouvu podepsanou přes eSign.</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="klient-smlouvy">
      <AffilmasterDetailSectionHeader title="Smlouvy" description={`${rows.length} dokumentů klienta`} />
      <div className="smlouvy-head">
        <span>Dokument</span>
        <span>Podepsali</span>
        <span>Odesláno</span>
        <span>Podepsáno</span>
        <span>Stav</span>
        <span />
      </div>
      <ol className="smlouvy-list">
        {rows.map((r) => {
          const signed = r.signers.filter((s) => s.status === 'signed').length;
          const done = r.status === 'completed';
          return (
            <ArcPanel as="li" className="smlouvy-radek" key={r.id}>
              <span className="smlouvy-nazev">
                {onOpenDocument
                  ? <button type="button" className="smlouvy-odkaz" onClick={() => onOpenDocument(r.id)}>{r.title}</button>
                  : r.title}
              </span>
              <span className="smlouvy-osoby">
                {r.signers.map((s) => (
                  <span key={`${s.name}-${s.companyName ?? ''}`} className={s.status === 'signed' ? 'je-podepsano' : ''}>
                    {s.name}{s.companyName ? `, ${s.companyName}` : ''}
                  </span>
                ))}
              </span>
              <span className="smlouvy-datum">{day(r.sentAt)}</span>
              <span className="smlouvy-datum">{day(r.completedAt)}</span>
              <span className={`smlouvy-stav${done ? ' je-hotovo' : ''}`}>
                {done ? 'Podepsáno' : `Čeká na podpis ${signed}/${r.signers.length}`}
              </span>
              <span className="smlouvy-akce">
                {done && (
                  <>
                    <a
                      className="smlouvy-soubor"
                      href={partnerEsignFileUrl(id, r.id, 'signed')}
                      title="Stáhnout podepsaný dokument"
                      aria-label={`Stáhnout podepsaný dokument ${r.title}`}
                    >
                      <DownloadIcon />
                      <span>Dokument</span>
                    </a>
                    <a
                      className="smlouvy-soubor"
                      href={partnerEsignFileUrl(id, r.id, 'certificate')}
                      title="Stáhnout certifikát o podpisu"
                      aria-label={`Stáhnout certifikát k dokumentu ${r.title}`}
                    >
                      <DownloadIcon />
                      <span>Certifikát</span>
                    </a>
                  </>
                )}
              </span>
            </ArcPanel>
          );
        })}
      </ol>
    </div>
  );
}
